import React from 'react';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'red' | 'yellow' | 'gray' | 'orange';
  onClick?: () => void;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  onClick,
}) => {
  const gradientClasses = {
    blue: 'from-blue-500 via-blue-600 to-blue-700',
    green: 'from-green-500 via-green-600 to-green-700',
    red: 'from-red-500 via-red-600 to-red-700',
    yellow: 'from-yellow-500 via-yellow-600 to-yellow-700',
    gray: 'from-gray-500 via-gray-600 to-gray-700',
    orange: 'from-orange-500 via-orange-600 to-orange-700',
  };

  const iconGradientClasses = {
    blue: 'from-blue-400 to-blue-600',
    green: 'from-green-400 to-green-600',
    red: 'from-red-400 to-red-600',
    yellow: 'from-yellow-400 to-yellow-600',
    gray: 'from-gray-400 to-gray-600',
    orange: 'from-orange-400 to-orange-600',
  };

  const lightGradientClasses = {
    blue: 'from-blue-50 via-blue-100/50 to-white dark:from-blue-900/30 dark:via-blue-800/20 dark:to-gray-800',
    green: 'from-green-50 via-green-100/50 to-white dark:from-green-900/30 dark:via-green-800/20 dark:to-gray-800',
    red: 'from-red-50 via-red-100/50 to-white dark:from-red-900/30 dark:via-red-800/20 dark:to-gray-800',
    yellow: 'from-yellow-50 via-yellow-100/50 to-white dark:from-yellow-900/30 dark:via-yellow-800/20 dark:to-gray-800',
    gray: 'from-gray-50 via-gray-100/50 to-white dark:from-gray-800/50 dark:via-gray-700/30 dark:to-gray-800',
    orange: 'from-orange-50 via-orange-100/50 to-white dark:from-orange-900/30 dark:via-orange-800/20 dark:to-gray-800',
  };

  const borderColorClasses = {
    blue: 'border-blue-200 dark:border-blue-700/50',
    green: 'border-green-200 dark:border-green-700/50',
    red: 'border-red-200 dark:border-red-700/50',
    yellow: 'border-yellow-200 dark:border-yellow-700/50',
    gray: 'border-gray-200 dark:border-gray-700/50',
    orange: 'border-orange-200 dark:border-orange-700/50',
  };

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${lightGradientClasses[color]} border ${borderColorClasses[color]} shadow-lg hover:shadow-2xl transition-all duration-300 ${
        onClick ? 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]' : ''
      }`}
      onClick={onClick}
    >
      {/* Gradient overlay on hover */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradientClasses[color]} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
      
      {/* Decorative corner gradient */}
      <div className={`absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br ${gradientClasses[color]} opacity-10 rounded-full blur-2xl`}></div>
      
      <div className="relative p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">
              {title}
            </p>
            <p className="text-4xl font-bold text-gray-900 dark:text-white mt-2">
              {value.toLocaleString('tr-TR')}
            </p>
          </div>
          
          {/* Icon with gradient background */}
          <div className={`relative ml-4 p-4 rounded-2xl bg-gradient-to-br ${iconGradientClasses[color]} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
            <div className="text-white text-2xl">
              {icon}
            </div>
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl"></div>
          </div>
        </div>

        {/* Bottom accent line */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${gradientClasses[color]} opacity-60`}></div>
      </div>

      {/* Click indicator */}
      {onClick && (
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${gradientClasses[color]}`}></div>
        </div>
      )}
    </div>
  );
};
